import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "./Sebar.css";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHeart,
  faComment,
  faShareNodes,
  faStar,
  faPaperPlane,
  faPlay,
  faTrophy,
  faUsers,
  faFire,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";

import { apiFetch, getCurrentUser } from "../services/api";
import { ListState } from "../components/ListState";
import { postAuthorId } from "../constants/navigation";
import { isValidYouTubeUrl, toYouTubeEmbedUrl } from "../utils/youtube";

function userLikedPost(post, user) {
  if (post.likedByMe) return true;
  if (!user?.id || !Array.isArray(post.likes)) return false;
  return post.likes.some((id) => String(id) === String(user.id));
}

export default function Sebar() {
  const [videoLink, setVideoLink] = useState("");
  const [caption, setCaption] = useState("");
  const [posts, setPosts] = useState([]);
  const [commentInputs, setCommentInputs] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [guruFilter, setGuruFilter] = useState({ sekolah: "", kelas: "" });
  const [scoreModal, setScoreModal] = useState({ open: false, postId: null, value: 80 });
  const [submittingScore, setSubmittingScore] = useState(false);

  const user = getCurrentUser();
  const token = getCurrentUser() ? localStorage.getItem("token") : null;

  const requireLogin = useCallback((actionLabel = "fitur ini") => {
    if (token) return true;
    toast.info(`Silakan login untuk menggunakan ${actionLabel}.`);
    return false;
  }, [token]);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    setFetchError("");
    try {
      const params = new URLSearchParams();
      if (user?.role === "guru") {
        if (guruFilter.sekolah.trim()) params.set("sekolah", guruFilter.sekolah.trim());
        if (guruFilter.kelas.trim()) params.set("kelas", guruFilter.kelas.trim());
      }
      const qs = params.toString();
      const res = await apiFetch(`/posts${qs ? `?${qs}` : ""}`);
      if (res.status === 401) {
        setFetchError("Sesi habis. Silakan login ulang.");
        setPosts([]);
        return;
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setFetchError(err.message || "Gagal memuat karya");
        setPosts([]);
        return;
      }
      const data = await res.json();
      setPosts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setFetchError("Tidak dapat terhubung ke server. Coba refresh halaman.");
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, [guruFilter.kelas, guruFilter.sekolah, user?.role]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handlePost = async () => {
    if (!requireLogin("mengunggah karya")) return;
    if (!videoLink.trim() || !caption.trim()) {
      toast.warn("Isi link YouTube dan refleksi dulu!");
      return;
    }
    if (!isValidYouTubeUrl(videoLink)) {
      toast.warn("Link harus berupa URL YouTube yang valid (watch, youtu.be, atau embed).");
      return;
    }
    try {
      const res = await apiFetch("/posts", {
        method: "POST",
        body: JSON.stringify({ videoLink: videoLink.trim(), caption: caption.trim() }),
      });
      if (!res.ok) {
        const err = await res.json();
        toast.error(err.message || "Gagal posting");
        return;
      }
      const newPost = await res.json();
      setPosts([newPost, ...posts]);
      setVideoLink("");
      setCaption("");
      toast.success("Karya berhasil diunggah!");
    } catch (err) {
      console.error(err);
      toast.error("Gagal posting. Coba lagi.");
    }
  };

  const toggleLike = async (postId) => {
    if (!requireLogin("memberi apresiasi")) return;
    try {
      const res = await apiFetch(`/posts/${postId}/like`, { method: "POST" });
      if (!res.ok) throw new Error("Gagal like");
      await fetchPosts();
    } catch (err) {
      console.error(err);
      toast.error("Gagal memberi apresiasi. Coba lagi.");
    }
  };

  const handleComment = async (postId) => {
    if (!requireLogin("mengomentari")) return;
    const text = commentInputs[postId];
    if (!text?.trim()) return;
    try {
      const res = await apiFetch(`/posts/${postId}/comment`, {
        method: "POST",
        body: JSON.stringify({ text: text.trim() }),
      });
      if (!res.ok) {
        const err = await res.json();
        toast.error(err.message || "Gagal komentar");
        return;
      }
      await res.json();
      setCommentInputs({ ...commentInputs, [postId]: "" });
      fetchPosts();
      toast.success("Komentar terkirim");
    } catch (err) {
      console.error(err);
      toast.error("Gagal mengirim komentar");
    }
  };

  const handleShare = async (post) => {
    const shareText = post.caption
      ? `${post.caption}\n${post.videoLink}`
      : post.videoLink;
    try {
      if (navigator.share) {
        await navigator.share({
          title: post.caption || "Karya Digital Storytelling",
          text: shareText,
          url: post.videoLink,
        });
        return;
      }
      await navigator.clipboard.writeText(shareText);
      toast.success("Link karya disalin!");
    } catch (err) {
      if (err?.name === "AbortError") return;
      try {
        await navigator.clipboard.writeText(shareText);
        toast.success("Link karya disalin!");
      } catch {
        toast.error("Gagal membagikan link");
      }
    }
  };

  const handleDelete = async (postId) => {
    if (!requireLogin("menghapus karya")) return;
    if (!window.confirm("Hapus karya ini dari galeri?")) return;
    try {
      const res = await apiFetch(`/posts/${postId}`, { method: "DELETE" });
      if (!res.ok) {
        const err = await res.json();
        toast.error(err.message || "Gagal menghapus karya");
        return;
      }
      setPosts((prev) => prev.filter((p) => p._id !== postId));
      toast.success("Karya dihapus");
    } catch (err) {
      console.error(err);
      toast.error("Gagal menghapus karya");
    }
  };

  const openScoreModal = (post) => {
    setScoreModal({
      open: true,
      postId: post._id,
      value: typeof post.score === "number" ? post.score : 80,
    });
  };

  const closeScoreModal = () => {
    setScoreModal({ open: false, postId: null, value: 80 });
  };

  const handleScoreSubmit = async () => {
    const { postId, value } = scoreModal;
    const score = Number(value);
    if (!postId || Number.isNaN(score) || score < 0 || score > 100) {
      toast.warn("Nilai harus antara 0–100");
      return;
    }
    setSubmittingScore(true);
    try {
      const res = await apiFetch(`/posts/${postId}/score`, {
        method: "POST",
        body: JSON.stringify({ score }),
      });
      if (!res.ok) {
        const err = await res.json();
        toast.error(err.message || "Gagal memberi nilai");
        return;
      }
      fetchPosts();
      closeScoreModal();
      toast.success("Nilai berhasil diberikan!");
    } catch (err) {
      console.error(err);
      toast.error("Gagal memberi nilai");
    } finally {
      setSubmittingScore(false);
    }
  };

  const filteredPosts = Array.isArray(posts)
    ? posts.filter((post) => {
        if (activeFilter === "popular") return (post.likes?.length || 0) >= 3;
        if (activeFilter === "highScore") return typeof post.score === "number" && post.score >= 80;
        return true;
      })
    : [];

  const postsArray = Array.isArray(posts) ? posts : [];
  const totalPosts = postsArray.length;
  const totalLikes = postsArray.reduce((sum, post) => sum + (post.likes?.length || 0), 0);
  const topScore =
    postsArray.length > 0
      ? Math.max(...postsArray.map((p) => (typeof p.score === "number" ? p.score : 0)))
      : 0;

  const canDeletePost = (post) =>
    user &&
    (user.role === "guru" ||
      String(postAuthorId(post) || post.userId) === String(user.id));

  return (
    <div className="sebar-container">
      <div className="sebar-header">
        <div className="header-content">
          <h1 className="title"> Galeri Karya Digital</h1>
          <p className="subtitle">
            Pamerkan karya storytelling digitalmu dan dapatkan apresiasi dari komunitas!
          </p>
          {!token && (
            <p className="sebar-guest-note">
              Anda dapat melihat galeri tanpa login.{" "}
              <Link to="/login">Masuk</Link> untuk mengunggah, memberi apresiasi, atau komentar.
            </p>
          )}
        </div>
      </div>

      <div className="stats-section">
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-info">
            <div className="stat-value">{totalPosts}</div>
            <div className="stat-label">Total Karya</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">❤️</div>
          <div className="stat-info">
            <div className="stat-value">{totalLikes}</div>
            <div className="stat-label">Total Apresiasi</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🏆</div>
          <div className="stat-info">
            <div className="stat-value">{topScore}</div>
            <div className="stat-label">Nilai Tertinggi</div>
          </div>
        </div>
      </div>

      <div className="upload-section">
        <div className="upload-card">
          <div className="upload-header">
            <h2>🎥 Unggah Karya Baru</h2>
            <div className="upload-guide">
              <span>💡 Tips: Upload video ke YouTube, lalu tempel link-nya di sini</span>
            </div>
          </div>

          <div className="upload-form">
            <div className="form-group">
              <label>🔗 Link YouTube Video</label>
              <input
                type="url"
                placeholder="https://www.youtube.com/watch?v=... atau https://youtu.be/..."
                value={videoLink}
                onChange={(e) => setVideoLink(e.target.value)}
                className="form-input"
                disabled={!token}
              />
            </div>

            <div className="form-group">
              <label>📝 Ceritakan Karyamu</label>
              <textarea
                placeholder="Bagikan cerita dibalik pembuatan video ini..."
                rows="4"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                className="form-textarea"
                disabled={!token}
              />
            </div>

            <button className="post-btn" onClick={handlePost} disabled={!token}>
              <FontAwesomeIcon icon={faPlay} />
              {!token ? "Login untuk Posting" : "🎬 Publikasikan Karya!"}
            </button>
          </div>
        </div>
      </div>

      <div className="filter-section">
        <h3>🔍 Filter Karya</h3>
        <div className="filter-buttons">
          <button
            className={`filter-btn ${activeFilter === "all" ? "active" : ""}`}
            onClick={() => setActiveFilter("all")}
          >
            🌟 Semua Karya
          </button>
          <button
            className={`filter-btn ${activeFilter === "popular" ? "active" : ""}`}
            onClick={() => setActiveFilter("popular")}
          >
            🔥 Populer
          </button>
          <button
            className={`filter-btn ${activeFilter === "highScore" ? "active" : ""}`}
            onClick={() => setActiveFilter("highScore")}
          >
            🏆 Nilai Tinggi
          </button>
        </div>

        {user?.role === "guru" && (
          <div className="guru-filter-row">
            <input
              type="text"
              placeholder="Filter sekolah..."
              value={guruFilter.sekolah}
              onChange={(e) => setGuruFilter((f) => ({ ...f, sekolah: e.target.value }))}
              className="form-input guru-filter-input"
            />
            <input
              type="text"
              placeholder="Filter kelas..."
              value={guruFilter.kelas}
              onChange={(e) => setGuruFilter((f) => ({ ...f, kelas: e.target.value }))}
              className="form-input guru-filter-input"
            />
            <button type="button" className="filter-btn" onClick={fetchPosts}>
              Terapkan
            </button>
          </div>
        )}
      </div>

      <div className="feed-section">
        <ListState
          loading={loading}
          loadingVariant="skeleton"
          error={fetchError || null}
          empty={!loading && !fetchError && filteredPosts.length === 0}
          onRetry={fetchPosts}
          emptyProps={{
            icon: "🎬",
            loadingMessage: "Memuat karya inspiratif...",
            title: "Belum ada karya yang dipamerkan",
            message: "Jadilah yang pertama membagikan karya storytelling digital!",
            action: (
              <button
                className="cta-button"
                onClick={() =>
                  document.querySelector(".upload-section")?.scrollIntoView({ behavior: "smooth" })
                }
              >
                🎥 Buat Karya Pertama
              </button>
            ),
          }}
        >
          <div className="posts-grid">
            {filteredPosts.map((post) => {
              const embedUrl = toYouTubeEmbedUrl(post.videoLink);
              const liked = userLikedPost(post, user);

              return (
                <div key={post._id} className="post-card">
                  <div className="post-header">
                    <div className="user-info">
                      <div className="avatar">{post.nama?.charAt(0) || "👤"}</div>
                      <div className="user-details">
                        {postAuthorId(post) ? (
                          <Link to={`/profile/${postAuthorId(post)}`} className="post-author-link">
                            {post.nama || "Creator"}
                          </Link>
                        ) : (
                          <h3>{post.nama || "Creator"}</h3>
                        )}
                        <div className="post-meta">
                          <span className="timestamp">
                            {new Date(post.createdAt).toLocaleDateString("id-ID")}
                          </span>
                          {(post.namaSekolah || post.kelas) && (
                            <span className="school-badge">
                              {[post.namaSekolah, post.kelas && `Kelas ${post.kelas}`]
                                .filter(Boolean)
                                .join(" · ")}
                            </span>
                          )}
                          {typeof post.score === "number" && (
                            <span className="score-badge">
                              <FontAwesomeIcon icon={faStar} /> {post.score}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="post-header-actions">
                      {typeof post.score === "number" && post.score >= 90 && (
                        <div className="featured-badge">
                          <FontAwesomeIcon icon={faTrophy} /> Featured
                        </div>
                      )}
                      {canDeletePost(post) && (
                        <button
                          type="button"
                          className="delete-post-btn"
                          onClick={() => handleDelete(post._id)}
                          title="Hapus karya"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="video-container">
                    {embedUrl ? (
                      <div className="video-wrapper">
                        <iframe
                          width="100%"
                          height="300"
                          src={embedUrl}
                          title="Video Storytelling"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    ) : (
                      <div className="video-unavailable">
                        <p>Video tidak dapat ditampilkan — periksa link YouTube.</p>
                        {post.videoLink && (
                          <a href={post.videoLink} target="_blank" rel="noopener noreferrer">
                            Buka di YouTube
                          </a>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="caption-section">
                    <p className="caption">{post.caption || "—"}</p>
                  </div>

                  <div className="post-actions">
                    <button
                      className={`action-btn like-btn ${liked ? "liked" : ""}`}
                      onClick={() => toggleLike(post._id)}
                    >
                      <FontAwesomeIcon icon={faHeart} />
                      <span>{post.likes?.length || 0}</span>
                    </button>

                    <button className="action-btn comment-btn" type="button">
                      <FontAwesomeIcon icon={faComment} />
                      <span>{post.comments?.length || 0}</span>
                    </button>

                    <button className="action-btn share-btn" onClick={() => handleShare(post)}>
                      <FontAwesomeIcon icon={faShareNodes} />
                      <span>Bagikan</span>
                    </button>

                    {user?.role === "guru" && (
                      <button
                        className="action-btn score-btn"
                        onClick={() => openScoreModal(post)}
                      >
                        <FontAwesomeIcon icon={faStar} />
                        <span>Nilai</span>
                      </button>
                    )}
                  </div>

                  <div className="comments-section">
                    {post.comments?.slice(0, 3).map((c) => (
                      <div key={c._id || c.createdAt} className="comment-item">
                        <div className="comment-avatar">{c.nama?.charAt(0) || "👤"}</div>
                        <div className="comment-content">
                          <div className="comment-header">
                            <strong>{c.nama}</strong>
                            <span className="comment-time">
                              {new Date(c.createdAt).toLocaleDateString("id-ID")}
                            </span>
                          </div>
                          <p className="comment-text">{c.text}</p>
                        </div>
                      </div>
                    ))}

                    {post.comments?.length > 3 && (
                      <div className="view-more-comments">
                        + {post.comments.length - 3} komentar lainnya
                      </div>
                    )}

                    <div className="comment-input-container">
                      <input
                        type="text"
                        placeholder={
                          token
                            ? "Tulis apresiasi atau komentar..."
                            : "Login untuk menulis komentar..."
                        }
                        value={commentInputs[post._id] || ""}
                        onChange={(e) =>
                          setCommentInputs({
                            ...commentInputs,
                            [post._id]: e.target.value,
                          })
                        }
                        className="comment-input"
                        disabled={!token}
                        onKeyPress={(e) => e.key === "Enter" && handleComment(post._id)}
                      />
                      <button
                        className="comment-submit"
                        onClick={() => handleComment(post._id)}
                        disabled={!token}
                      >
                        <FontAwesomeIcon icon={faPaperPlane} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </ListState>
      </div>

      {scoreModal.open && (
        <div className="sebar-modal-overlay" onClick={closeScoreModal}>
          <div
            className="sebar-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="score-modal-title"
          >
            <h3 id="score-modal-title">Berikan Nilai Karya</h3>
            <p className="sebar-modal-desc">Nilai 0–100 untuk karya siswa</p>
            <div className="sebar-score-display">{scoreModal.value}</div>
            <input
              type="range"
              min="0"
              max="100"
              value={scoreModal.value}
              onChange={(e) =>
                setScoreModal((s) => ({ ...s, value: Number(e.target.value) }))
              }
              className="sebar-score-slider"
            />
            <input
              type="number"
              min="0"
              max="100"
              value={scoreModal.value}
              onChange={(e) =>
                setScoreModal((s) => ({ ...s, value: Number(e.target.value) || 0 }))
              }
              className="sebar-score-input"
            />
            <div className="sebar-modal-actions">
              <button type="button" className="sebar-modal-cancel" onClick={closeScoreModal}>
                Batal
              </button>
              <button
                type="button"
                className="sebar-modal-submit"
                onClick={handleScoreSubmit}
                disabled={submittingScore}
              >
                {submittingScore ? "Menyimpan…" : "Simpan Nilai"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="sebar-footer">
        <div className="footer-content">
          <h3>🎉 Terus Berkarya dan Berbagi!</h3>
          <p>Setiap karya adalah cerita unik yang layak untuk dibagikan</p>
          <div className="footer-stats">
            <div className="footer-stat">
              <FontAwesomeIcon icon={faUsers} />
              <span>Komunitas Kreatif</span>
            </div>
            <div className="footer-stat">
              <FontAwesomeIcon icon={faFire} />
              <span>Trending Karya</span>
            </div>
            <div className="footer-stat">
              <FontAwesomeIcon icon={faTrophy} />
              <span>Penghargaan</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
